import { useQuery } from "@tanstack/react-query";
import { UserProfileDto, UserRole, StakeholderDto } from "@green0meter/api-client";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";

import { TANSTACK_QUERY_STALE_TIME } from "@constants";
import { roleGuard } from "@api";
import { doubleMaterialityStakeholdersApi } from "@api/services";


export const ACTION_GET_STAKEHOLDERS = "GET_STAKEHOLDERS";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

interface GetStakeholdersRequest {
  search?: string;
  orderBy?: string;
  orderDirection?: string;
  pageNumber?: number;
  pageSize?: number;
  year: number;
}

interface GetStakeholdersResponse {
  data: StakeholderDto[];
  totalItems: number;
  pageNumber: number;
  pageSize: number;
}

const getStakeholders = async (
  params: GetStakeholdersRequest,
): Promise<GetStakeholdersResponse> => {
  const response =
    await doubleMaterialityStakeholdersApi.doubleMaterialityStakeholdersControllerGetAllStakeholders(
      params.year,
      params?.search,
      params?.orderBy,
      params?.orderDirection,
      params?.pageNumber,
      params?.pageSize,
    );

  const pageSize = params?.pageSize || 10;
  const pageNumber = params?.pageNumber || 1;

  return {
    data: response.data.items,
    totalItems: response.data.totalCount,
    pageNumber,
    pageSize,
  };
};

export const useGetStakeholders = (
  user: UserProfileDto | null,
  params: GetStakeholdersRequest,
): {
  stakeholders?: GetStakeholdersResponse;
  stakeholdersEnabled: boolean;
  isPendingGetStakeholders: boolean;
  refetchStakeholders: () => void;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const { isPending, data, refetch } = useQuery({
    queryKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_STAKEHOLDERS, params],
    queryFn: () => getStakeholders(params),
    staleTime: TANSTACK_QUERY_STALE_TIME,
    enabled,
  });

  return {
    stakeholders: data,
    stakeholdersEnabled: enabled,
    isPendingGetStakeholders: isPending,
    refetchStakeholders: refetch,
  };
};
